import React, { useState } from 'react';
import { 
  Plus, 
  Calendar, 
  MessageCircle, 
  User, 
  Search, 
  Package,
  BarChart3,
  Heart,
  Star,
  Clock,
  TrendingUp,
  Eye,
  Edit
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface QuickAction {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  action: () => void;
  color: string;
  bgColor: string;
  count?: number;
}

interface DashboardStats {
  totalAppointments: number;
  unreadMessages: number;
  activeServices: number;
  monthlyEarnings: number;
  newReviews: number;
  profileViews: number;
}

const QuickActionsDashboard = () => {
  const navigate = useNavigate();
  
  // Mock stats - in real app would come from API
  const [stats] = useState<DashboardStats>({
    totalAppointments: 12,
    unreadMessages: 3,
    activeServices: 4,
    monthlyEarnings: 1450,
    newReviews: 2,
    profileViews: 89
  });

  const quickActions: QuickAction[] = [
    {
      id: 'new-service',
      title: 'Nieuwe Dienst',
      description: 'Voeg een nieuwe service toe',
      icon: Plus,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50 hover:bg-blue-100 border-blue-200',
      action: () => {
        navigate('/add-service');
        toast.info('Nieuwe dienst toevoegen...');
      }
    },
    {
      id: 'calendar',
      title: 'Agenda',
      description: 'Bekijk je afspraken',
      icon: Calendar,
      color: 'text-green-600',
      bgColor: 'bg-green-50 hover:bg-green-100 border-green-200',
      count: stats.totalAppointments,
      action: () => {
        navigate('/agenda');
        toast.info('Agenda openen...');
      }
    },
    {
      id: 'messages',
      title: 'Berichten',
      description: 'Nieuwe berichten lezen',
      icon: MessageCircle,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50 hover:bg-purple-100 border-purple-200',
      count: stats.unreadMessages,
      action: () => {
        navigate('/berichten');
        toast.info('Berichten openen...');
      }
    },
    {
      id: 'profile',
      title: 'Mijn Profiel',
      description: 'Profiel beheren',
      icon: User,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50 hover:bg-orange-100 border-orange-200',
      action: () => {
        navigate('/profiel');
        toast.info('Profiel openen...');
      }
    },
    {
      id: 'marketplace',
      title: 'Marketplace',
      description: 'Services ontdekken',
      icon: Search,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50 hover:bg-indigo-100 border-indigo-200',
      action: () => {
        navigate('/marketplace');
        toast.info('Marketplace verkennen...');
      }
    },
    {
      id: 'products',
      title: 'Producten',
      description: 'Product toevoegen',
      icon: Package,
      color: 'text-pink-600',
      bgColor: 'bg-pink-50 hover:bg-pink-100 border-pink-200',
      count: stats.activeServices,
      action: () => {
        navigate('/add-product');
        toast.info('Product toevoegen...');
      }
    }
  ];

  const statsCards = [
    {
      title: 'Maandelijkse Verdiensten',
      value: `€${stats.monthlyEarnings}`,
      icon: TrendingUp,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      title: 'Nieuwe Reviews',
      value: stats.newReviews.toString(),
      icon: Star,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50'
    },
    {
      title: 'Profiel Bekeken',
      value: stats.profileViews.toString(),
      icon: Eye,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-3 gap-4">
        {statsCards.map((stat, index) => (
          <Card key={index} className={`${stat.bgColor} border-2 transition-all hover:shadow-md`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <stat.icon className={`h-8 w-8 ${stat.color}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions Grid */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-serif text-navy-800">
            Snelle Acties
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {quickActions.map((action) => (
              <Button
                key={action.id}
                onClick={action.action}
                variant="outline"
                className={`${action.bgColor} border-2 h-auto p-4 flex flex-col items-center gap-2 transition-all duration-200 hover:scale-105 hover:shadow-md relative`}
              >
                {action.count && action.count > 0 && (
                  <Badge className="absolute -top-2 -right-2 bg-red-500 text-white text-xs">
                    {action.count}
                  </Badge>
                )}
                <action.icon className={`h-8 w-8 ${action.color}`} />
                <div className="text-center">
                  <p className="font-medium text-gray-900">{action.title}</p>
                  <p className="text-xs text-gray-600">{action.description}</p>
                </div>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-serif text-navy-800">
            Recente Activiteit
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-cream-50 rounded-lg">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <Calendar className="h-4 w-4 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-navy-800">Afspraak bevestigd</p>
                <p className="text-xs text-navy-600">Marketing coaching met Sarah - morgen 14:00</p>
              </div>
              <span className="text-xs text-navy-500">30 min</span>
            </div>

            <div className="flex items-center gap-3 p-3 bg-cream-50 rounded-lg">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <MessageCircle className="h-4 w-4 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-navy-800">Nieuw bericht</p>
                <p className="text-xs text-navy-600">Lisa heeft je een bericht gestuurd</p>
              </div>
              <span className="text-xs text-navy-500">2 uur</span>
            </div>

            <div className="flex items-center gap-3 p-3 bg-cream-50 rounded-lg">
              <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                <Star className="h-4 w-4 text-yellow-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-navy-800">Review ontvangen</p>
                <p className="text-xs text-navy-600">5 sterren voor je Breathwork sessie</p>
              </div>
              <span className="text-xs text-navy-500">4 uur</span>
            </div>
          </div>
          
          <Button 
            variant="ghost" 
            className="w-full mt-4 text-navy-600 hover:bg-cream-100"
            onClick={() => {
              toast.info('Alle activiteit bekijken...');
              console.log('View all activity');
            }}
          >
            Alle activiteit bekijken
          </Button>
        </CardContent>
      </Card>

      {/* WOM Plaza Tip */}
      <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Clock className="h-4 w-4 text-amber-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-amber-900 mb-1">Tip van de dag</h3>
              <p className="text-sm text-amber-800 leading-relaxed">
                Voeg foto's toe aan je services om 40% meer boekingen te krijgen! 
                Visuele content trekt meer aandacht van potentiële klanten.
              </p>
              <Button 
                size="sm" 
                className="mt-2 bg-amber-600 hover:bg-amber-700 text-white"
                onClick={() => {
                  navigate('/profiel');
                  toast.success('Ga naar profiel om foto\'s toe te voegen');
                }}
              >
                Foto's toevoegen
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default QuickActionsDashboard;